// ─────────────────────────────────────────────────────────────────────────────
// ¿Se puede deshacer el pánico de esta campaña? Espejo de `Campaign.DeshacerPanico` del backend
// (RecognitionService), que exige tres cosas: que se haya cerrado POR PÁNICO, que fuera hace menos de
// 24 h y que su fecha de fin no haya pasado. Vive junto al contrato, como `limits.ts`, porque la
// regla la fija el backend: la pantalla y el mock la consumen desde aquí en vez de repetirla.
//
// El contrato no trae la ventana, así que se replica como constante. El rechazo del servidor sigue
// siendo la red de seguridad (desfase de reloj entre navegador y servidor).
// ─────────────────────────────────────────────────────────────────────────────

import type { CampaignDto } from './types';

/**
 * Debe coincidir con `Campaign.VentanaDeshacerPanico` del backend (24 h). En el instante exacto de las
 * 24 h el backend todavía acepta (rechaza con `>`); aquí ya no, a propósito: la historia pide «menos de
 * 24 horas» y adelantarse ese milisegundo no le quita a nadie una reactivación real.
 */
export const VENTANA_DESHACER_PANICO_MS = 24 * 60 * 60 * 1000;

/** Por qué no se puede deshacer el pánico, en el orden en que lo comprueba el backend. */
export type MotivoSinDeshacerPanico = 'sin-panico' | 'ventana-vencida' | 'vigencia-vencida';

type CampanaPanico = Pick<CampaignDto, 'estado' | 'finalizadaPorPanicoAt' | 'fechaFin'>;

/**
 * `null` si el pánico se puede deshacer ahora: Finalizada por pánico (una vencida por fecha no trae
 * `finalizadaPorPanicoAt`), hace menos de 24 h y con la fecha de fin (si tiene) sin pasar.
 */
export function motivoSinDeshacerPanico(campana: CampanaPanico): MotivoSinDeshacerPanico | null {
  if (campana.estado !== 'Finalizada' || !campana.finalizadaPorPanicoAt) return 'sin-panico';
  const ahora = Date.now();
  const cerradaEn = Date.parse(campana.finalizadaPorPanicoAt);
  if (Number.isNaN(cerradaEn) || ahora - cerradaEn >= VENTANA_DESHACER_PANICO_MS) return 'ventana-vencida';
  const fin = campana.fechaFin ? Date.parse(campana.fechaFin) : NaN;
  if (!Number.isNaN(fin) && ahora > fin) return 'vigencia-vencida';
  return null;
}

/** ¿Se ofrece «Deshacer pánico»? Ver `motivoSinDeshacerPanico`. */
export function puedeDeshacerPanico(campana: CampanaPanico): boolean {
  return motivoSinDeshacerPanico(campana) === null;
}
